import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authMe, authLogin, authSignup } from "./api";

const AuthContext = createContext(null);
const TOKEN_KEY = "tc_auth_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const hydrate = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    try {
      const me = await authMe();
      setUser(me);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { hydrate(); }, [hydrate]);

  const login = async (email, password) => {
    const { token, user: u } = await authLogin({ email, password });
    localStorage.setItem(TOKEN_KEY, token);
    setUser(u);
    return u;
  };

  const signup = async (email, password, name) => {
    const { token, user: u } = await authSignup({ email, password, name });
    localStorage.setItem(TOKEN_KEY, token);
    setUser(u);
    return u;
  };

  // iter82 — used by AuthCallback after a successful Google sign-in.
  // Stores the JWT the backend issued and sets the user in state so all
  // downstream pages ({/account, /checkout, etc.}) see the user
  // immediately without needing another /auth/me round-trip.
  const setSessionFromGoogle = ({ token, user: u }) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser(u);
    return u;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refresh: hydrate, setSessionFromGoogle }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
